import { useQuery, useSuspenseQuery } from '@tanstack/react-query';
import { Link } from '@tanstack/react-router';
import type { IncidentListItemView } from '@wodi/shared';
import {
  buttonLinkProps,
  Cell,
  Column,
  LoadingBlock,
  PageHeader,
  Row,
  Section,
  SegmentedControl,
  Table,
  TableBody,
  TableHeader,
} from '@wodi/ui';
import { useState } from 'react';

import { incidentListQuery, myIncidentsQuery, type IncidentFilter } from '../api/incidents.ts';
import { can, sessionQuery } from '../api/session.ts';
import { useOnline } from '../hooks/use-online.ts';
import { TableScroll } from '../layout/screen.tsx';
import {
  dateTimeText,
  LoadFailed,
  NotAllowed,
  OfflineListNotice,
  plural,
} from '../operations/format.tsx';
import styles from '../operations/operations.module.css';
import { IncidentStatusTag } from './words.tsx';

// Incidents (docs/plans/phase3-operations-modules.md §1.4): the register for holders of
// `incidents.read`, by status, and "My reports" for everyone who reports. Opening a list is
// audited on the server with a count.

function IncidentTable({
  label,
  items,
  empty,
}: {
  label: string;
  items: IncidentListItemView[];
  empty: string;
}) {
  return (
    <TableScroll>
      <Table flat aria-label={label}>
        <TableHeader>
          <Column isRowHeader>Number</Column>
          <Column>Happened</Column>
          <Column>Category</Column>
          <Column>Harm</Column>
          <Column>Where</Column>
          <Column>Status</Column>
          <Column>Actions</Column>
        </TableHeader>
        <TableBody items={items} renderEmptyState={() => empty}>
          {(i) => (
            <Row id={i.id}>
              <Cell>
                <Link to="/incidents/$incidentId" params={{ incidentId: i.id }}>
                  <span className="wodi-id">{i.incidentNumber}</span>
                </Link>
              </Cell>
              <Cell>{dateTimeText(i.occurredAt)}</Cell>
              <Cell>{i.category.display}</Cell>
              <Cell>{i.severity.display}</Cell>
              <Cell>{i.location}</Cell>
              <Cell>
                <IncidentStatusTag status={i.status} />
              </Cell>
              <Cell>
                {i.openActions === 0
                  ? 'None open'
                  : `${plural(i.openActions, 'open', 'open')}${i.overdueActions > 0 ? `, ${String(i.overdueActions)} overdue` : ''}`}
              </Cell>
            </Row>
          )}
        </TableBody>
      </Table>
    </TableScroll>
  );
}

function Register() {
  const [filter, setFilter] = useState<IncidentFilter>('open');
  const list = useQuery(incidentListQuery(filter));
  return (
    <Section
      title="Register"
      description="Incidents reported at this facility, the latest first."
      actions={
        <SegmentedControl
          aria-label="Which incidents"
          value={filter}
          onChange={(value) => {
            setFilter(value as IncidentFilter);
          }}
          options={[
            { id: 'open', label: 'Open' },
            { id: 'closed', label: 'Closed' },
            { id: 'all', label: 'All' },
          ]}
        />
      }
    >
      {list.isPending ? <LoadingBlock label="Loading incidents" rows={5} /> : null}
      {list.isError ? (
        <LoadFailed
          error={list.error}
          fallback="Incidents couldn't be loaded. Try again."
          onRetry={() => void list.refetch()}
        />
      ) : null}
      {list.isSuccess ? (
        <IncidentTable
          label="Incidents"
          items={list.data.items}
          empty={
            filter === 'open'
              ? 'No open incidents. New reports appear here.'
              : filter === 'closed'
                ? 'No closed incidents yet.'
                : 'No incidents reported yet.'
          }
        />
      ) : null}
    </Section>
  );
}

function MyReports() {
  const list = useQuery(myIncidentsQuery);
  return (
    <Section title="My reports" description="Incidents you reported here, the latest first.">
      {list.isPending ? <LoadingBlock label="Loading your reports" rows={3} /> : null}
      {list.isError ? (
        <LoadFailed
          error={list.error}
          fallback="Your reports couldn't be loaded. Try again."
          onRetry={() => void list.refetch()}
        />
      ) : null}
      {list.isSuccess ? (
        <IncidentTable
          label="My reports"
          items={list.data.items}
          empty="You haven't reported an incident here."
        />
      ) : null}
    </Section>
  );
}

export function IncidentsPage() {
  const { data: session } = useSuspenseQuery(sessionQuery);
  const online = useOnline();
  if (session === null || !can(session, 'incidents.report'))
    return <NotAllowed title="Incidents" what="see incidents" />;
  return (
    <div className={styles.page}>
      <PageHeader
        title="Incidents"
        description="Report what went wrong, or nearly did, so it can be put right."
        actions={
          <Link to="/incidents/new" {...buttonLinkProps({ variant: 'primary' })}>
            Report an incident
          </Link>
        }
      />
      <OfflineListNotice online={online} />
      {can(session, 'incidents.read') ? <Register /> : null}
      <MyReports />
    </div>
  );
}
